import React, { useState, useEffect } from 'react';
import { 
  FileText, BarChart2, Award, ChevronRight, Calendar, Clock, ArrowUp, 
  ArrowDown, Eye, BookOpen, CheckCircle, Share2
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Routes, Route, Link, useParams, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase/client';

import TestListView from './ExamResults/TestListView';
import TestDetailView from './ExamResults/TestDetailView';
import QuestionReviewView from './ExamResults/QuestionReviewView';
import ProgressChartView from './ExamResults/ProgressChartView';
import { DateTime } from 'luxon';

interface ExamStats {
  totalTestsCompleted: number;
  averageScore: number;
  highestScore: number;
  recentlyCompletedCount: number;
}

// Types to match the DB structure
interface ProfileTest {
  id: string;
  profile_id: string;
  test_id: string;
  completed_on: string;
  started_on: string;
  score: number;
  sections: any;
}

interface Test {
  id: string;
  test_name: string;
  description: string;
  level: string;
  category: string;
  sections: any;
}

interface Question {
  id: string;
  passage_id: string;
  prompt: string;
  explanation: string;
  hint: string;
  level: string;
}

// Shared layout component
export const ExamResultsLayout: React.FC = () => {
  const { data: examStats, isLoading: loadingExamStats } = useQuery({
    queryKey: ['ExamResults', { component: 'stats' }],
    queryFn: async() => {
      const { data, error } = await supabase.rpc('get_exam_stats').single();
      if (error) {
        console.log(error);
      }

      return data as unknown as ExamStats;
    }
  });

  const [showShareResultsModal, setShowShareResultsModal] = useState<boolean>(false);

  return (
    <div className="min-h-screen bg-linear-to-br from-blue-50 to-indigo-50">
      {/* Header section */}
      <div className="py-12 px-4 sm:px-6 lg:px-8 bg-linear-to-r from-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto">
          <div className="md:flex md:items-center md:justify-between">
            <div className="md:w-2/3">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Your Exam Results</h1>
              <p className="text-xl text-indigo-100 mb-6">
                Track your progress, analyze your performance, and improve your score through targeted practice.
              </p>
              <div className="flex space-x-4">
                <Link 
                  to="/exams" 
                  className="px-6 py-3 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition-colors shadow-md flex items-center"
                >
                  <FileText className="mr-2 h-5 w-5" />
                  Take New Exam
                </Link>
                {/* <button 
                  className="px-6 py-3 bg-indigo-500/30 text-white rounded-lg font-medium hover:bg-inherit/40 transition-colors border border-indigo-300 flex items-center"
                  onClick={() => setShowShareResultsModal(true)}
                >
                  <Share2 className="mr-2 h-5 w-5" />
                  Share Results
                </button> */}
              </div>
            </div>
            
            <div className="md:w-1/3 mt-8 md:mt-0">
              <div className="bg-white/10 rounded-xl p-4 border border-indigo-300/30">
                <div className="flex items-center mb-3">
                  <BarChart2 className="h-5 w-5 text-indigo-100 mr-2" />
                  <h3 className="text-white font-medium">Your Performance</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-indigo-100 text-sm">Tests Completed</p>
                    <p className="text-white font-bold text-2xl">{examStats?.totalTestsCompleted ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Average Score</p>
                    <p className="text-white font-bold text-2xl">{examStats?.averageScore ? `${examStats.averageScore}%` : '0%'}</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Highest Score</p>
                    <p className="text-white font-bold text-2xl">{examStats?.highestScore ? `${examStats.highestScore}%` : '0%'}</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">This Week</p>
                    <p className="text-white font-bold text-2xl">{examStats?.recentlyCompletedCount ?? 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Child routes will be rendered here */}
      <Outlet />

      {/* Shared Modals */}
      {showShareResultsModal && (
        <ShareResultsModal
          showShareResultsModal={showShareResultsModal}
          setShowShareResultsModal={setShowShareResultsModal}
        />
      )}
    </div>
  );
};

// Main Exam Results Page
export const ExamResultsMainPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Client states
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [selectedSort, setSelectedSort] = useState<string>('recent');
  const [showFilterDropdown, setShowFilterDropdown] = useState<boolean>(false);
  const [showSortDropdown, setShowSortDropdown] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  
  // Query hooks
  const queryClient = useQueryClient();
  
  // Create query params based on current filters
  const profileTestsParams = {
    searchQuery: searchQuery || undefined,
    filter: selectedFilter,
    sort: selectedSort,
    page: currentPage,
    limit: 10
  };
  
  // Fetch completed tests
  const { 
    data: profileTestsData, 
    isLoading: isLoadingProfileTests,
    isError: isErrorProfileTests
  } = useQuery({
    queryKey: ['ProfileTests', profileTestsParams],
    queryFn: async() => {
      if (!user?.id) return { profileTests: [], totalCount: 0 };
      
      // Get profile tests with joined test data
      const { data, error, count } = await supabase
        .from('profile_tests')
        .select(`
          *,
          tests:test_id(
            id,
            test_name,
            description,
            level,
            category
          )
        `, { count: 'exact' })
        .eq('profile_id', user.id)
        .not('completed_on', 'is', null)
        .order(selectedSort === 'recent' ? 'completed_on' : 'score', { ascending: selectedSort !== 'recent' })
        .range((currentPage - 1) * 10, currentPage * 10 - 1);
      
      if (error) {
        console.log(error);
        return { profileTests: [], totalCount: 0 };
      }
      
      return { 
        profileTests: data as (ProfileTest & { tests: Test })[], 
        totalCount: count ?? 0 
      };
    },
    enabled: !!user?.id
  });
  
  // Fetch recommended tests
  const { 
    data: recommendedTests, 
    isLoading: isLoadingRecommendedTests 
  } = useQuery({
    queryKey: ['RecommendedTests'],
    queryFn: async() => {
      if (!user?.id) return [];
      
      // This would use a more sophisticated algorithm in practice
      const { data, error } = await supabase
        .from('tests')
        .select('*')
        .limit(5);
      
      if (error) {
        console.log(error);
        return [];
      }
      
      return data as Test[];
    },
    enabled: !!user?.id
  });
  
  // Filter options
  const filterOptions = [
    { id: 'all', name: 'All Tests' },
    { id: 'high-scores', name: 'High Scores' },
    { id: 'recently-completed', name: 'Recently Completed' },
    { id: 'needs-improvement', name: 'Needs Improvement' }
  ];
  
  // Sort options
  const sortOptions = [
    { id: 'recent', name: 'Most Recent' },
    { id: 'score-high', name: 'Highest Score' },
    { id: 'score-low', name: 'Lowest Score' }
  ];
  
  // Get tests from the query result
  const profileTests = profileTestsData?.profileTests || [];
  const totalTestsCount = profileTestsData?.totalCount || 0;
  const totalPages = Math.ceil(totalTestsCount / 10);
  
  // Handle filter selection
  const handleFilterSelect = (filterId: string) => {
    setSelectedFilter(filterId);
    setCurrentPage(1);
    setShowFilterDropdown(false);
  };
  
  // Handle sort selection
  const handleSortSelect = (sortId: string) => {
    setSelectedSort(sortId);
    setCurrentPage(1);
    setShowSortDropdown(false);
  };
  
  // Function to handle test click
  const handleTestClick = (testId: string): void => {
    navigate(`test/${testId}`);
  };
  
  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="lg:flex lg:space-x-8">
        {/* Main content */}
        <div className="lg:w-2/3">
          <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <h2 className="text-2xl font-bold text-gray-800">Your Completed Tests</h2>
              
              <div className="flex space-x-3">
                {/* Filter dropdown */}
                <div className="relative">
                  <button
                    className="flex items-center text-gray-600 bg-gray-100 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  >
                    Filter
                    <ChevronRight className={`ml-1 h-4 w-4 transform transition-transform ${showFilterDropdown ? 'rotate-90' : ''}`} />
                  </button>
                  
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg z-10 border border-gray-100">
                      {filterOptions.map(option => (
                        <button
                          key={option.id}
                          className={`w-full text-left px-4 py-2 text-sm ${selectedFilter === option.id ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'}`}
                          onClick={() => handleFilterSelect(option.id)}
                        >
                          {option.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Sort dropdown */}
                <div className="relative">
                  <button
                    className="flex items-center text-gray-600 bg-gray-100 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                  >
                    Sort
                    <ChevronRight className={`ml-1 h-4 w-4 transform transition-transform ${showSortDropdown ? 'rotate-90' : ''}`} />
                  </button>
                  
                  {showSortDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg z-10 border border-gray-100">
                      {sortOptions.map(option => (
                        <button
                          key={option.id}
                          className={`w-full text-left px-4 py-2 text-sm ${selectedSort === option.id ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'}`}
                          onClick={() => handleSortSelect(option.id)}
                        >
                          {option.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Search bar */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by test name or category..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
            
            {/* Test list */}
            {isLoadingProfileTests ? (
              <div className="text-center py-10">
                <p className="text-gray-500">Loading your test results...</p>
              </div>
            ) : isErrorProfileTests ? (
              <div className="text-center py-10">
                <p className="text-red-500">Error loading your test results. Please try again.</p>
              </div>
            ) : profileTests.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-gray-300 rounded-lg">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-xl font-medium text-gray-700 mb-1">No completed tests yet</h3>
                <p className="text-gray-500 mb-4">Complete your first test to see your results here.</p>
                <Link
                  to="/exams"
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Browse Available Tests
                </Link>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {profileTests.map((profileTest) => (
                    <div
                      key={profileTest.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleTestClick(profileTest.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium text-lg text-gray-900">{profileTest.tests.test_name}</h3>
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <Calendar className="h-4 w-4 mr-1" />
                            <span>
                              {new Date(profileTest.completed_on).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                            <span className="mx-2">•</span>
                            <Clock className="h-4 w-4 mr-1" />
                            <span>
                              {getTimeSpent(profileTest.started_on ?? '', (DateTime.fromISO(profileTest.started_on ?? '') > DateTime.fromISO(profileTest.completed_on ?? '') ? profileTest.expiration_time : profileTest.completed_on) ?? '')}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <div className={`font-bold text-2xl ${getScoreColor(profileTest.score)}`}>
                            {profileTest.score}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {getScoreLabel(profileTest.score)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {profileTest.tests.category}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {profileTest.tests.level}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-8">
                    <nav className="relative z-0 inline-flex shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border ${
                          currentPage === 1
                            ? 'border-gray-300 bg-white text-gray-300 cursor-not-allowed'
                            : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronRight className="h-5 w-5 transform rotate-180" />
                      </button>
                      
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageToShow = currentPage > 3
                          ? currentPage - 3 + i + 1
                          : i + 1;
                          
                        if (pageToShow <= totalPages) {
                          return (
                            <button
                              key={pageToShow}
                              onClick={() => handlePageChange(pageToShow)}
                              className={`relative inline-flex items-center px-4 py-2 border ${
                                currentPage === pageToShow
                                  ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageToShow}
                            </button>
                          );
                        }
                        return null;
                      })}
                      
                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border ${
                          currentPage === totalPages
                            ? 'border-gray-300 bg-white text-gray-300 cursor-not-allowed'
                            : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="lg:w-1/3 mt-8 lg:mt-0">
          {/* Performance overview */}
          <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Performance Overview</h3>
            <Link 
              to="/exam-results/progress" 
              className="block text-indigo-600 hover:text-indigo-800 mb-6"
            >
              View detailed progress →
            </Link>
            
            <div className="space-y-4">
              <div className="relative pt-1">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <span className="text-xs font-medium text-gray-800">Logical Reasoning</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold text-gray-800">72%</span>
                  </div>
                </div>
                <div className="flex h-2 overflow-hidden rounded bg-gray-200">
                  <div className="bg-green-500" style={{ width: '72%' }}></div>
                </div>
              </div>
              
              <div className="relative pt-1">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <span className="text-xs font-medium text-gray-800">Reading Comprehension</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold text-gray-800">65%</span>
                  </div>
                </div>
                <div className="flex h-2 overflow-hidden rounded bg-gray-200">
                  <div className="bg-blue-500" style={{ width: '65%' }}></div>
                </div>
              </div>
              
              <div className="relative pt-1">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <span className="text-xs font-medium text-gray-800">Analytical Reasoning</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold text-gray-800">58%</span>
                  </div>
                </div>
                <div className="flex h-2 overflow-hidden rounded bg-gray-200">
                  <div className="bg-yellow-500" style={{ width: '58%' }}></div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Recommended tests */}
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Recommended For You</h3>
            {isLoadingRecommendedTests ? (
              <p className="text-gray-500">Loading recommendations...</p>
            ) : recommendedTests && recommendedTests.length > 0 ? (
              <div className="space-y-3">
                {recommendedTests.map((test) => (
                  <Link
                    key={test.id}
                    to={`/exams/${test.id}`}
                    className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <h4 className="font-medium text-gray-900">{test.test_name}</h4>
                    <div className="flex items-center text-sm text-gray-500 mt-1">
                      <BookOpen className="h-4 w-4 mr-1" />
                      <span>{test.category}</span>
                      <span className="mx-2">•</span>
                      <span>{test.level}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Complete more tests to get personalized recommendations.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Test Detail Page
export const ExamResultsDetailPage: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  
  // Fetch test result
  const { 
    data: testResult, 
    isLoading: isLoadingTestResult 
  } = useQuery({
    queryKey: ['TestResult', testId],
    queryFn: async() => {
      if (!testId) return null;
      
      const { data, error } = await supabase
        .from('profile_tests')
        .select(`
          *,
          tests:test_id(
            id,
            test_name,
            description,
            level,
            category,
            sections
          )
        `)
        .eq('id', testId)
        .single();
      
      if (error) {
        console.log(error);
        return null;
      }
      
      return data;
    },
    enabled: !!testId
  });
  
  // Fetch test questions
  const {
    data: testQuestions,
    isLoading: isLoadingTestQuestions
  } = useQuery({
    queryKey: ['TestQuestions', testId],
    queryFn: async() => {
      if (!testId) return [];
      
      const { data, error } = await supabase
        .from('test_questions')
        .select(`
          *,
          passages(
            id,
            question_type,
            prompt,
            question_text,
            questions(
              id,
              prompt,
              explanation,
              hint,
              level,
              answer_id,
              answers!answers_question_id_fkey(
                id,
                content,
                sorted
              )
            )
          )
        `)
        .eq('test_id', testResult?.test_id);
      
      if (error) {
        console.log(error);
        return [];
      }
      
      return data;
    },
    enabled: !!testId && !!testResult
  });
  
  // Analyze section performance
  const sectionPerformance = React.useMemo(() => {
    if (!testResult) return [];
    
    // This would be more sophisticated in practice
    const sections = testResult.sections || [];
    return Object.keys(sections).filter(s => sections[s].type === "section" && sections[s].scored === true).map((sectionKey, idx) => {
      const section = sections[sectionKey];
      return {
        name: `Section ${idx+1}: ${section.category}`,
        correctCount: section.correctCount || 0,
        totalCount: section.totalCount || 0,
        score: section.score || 0
      };
    });
  }, [testResult]);
  
  // Handle question click
  const handleQuestionClick = (questionId: string) => {
    navigate(`question/${questionId}`);
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {isLoadingTestResult ? (
        <div className="text-center py-10">
          <p className="text-gray-500">Loading test results...</p>
        </div>
      ) : !testResult ? (
        <div className="text-center py-10">
          <p className="text-red-500">Test result not found.</p>
          <button
            onClick={() => navigate('/exam-results')}
            className="mt-4 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to Results
          </button>
        </div>
      ) : (
        <div className="lg:flex lg:space-x-8">
          {/* Main content */}
          <div className="lg:w-2/3">
            <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">{testResult.tests.test_name}</h2>
                  <div className="flex items-center text-sm text-gray-500 mt-1">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>
                      {new Date(testResult.completed_on ?? '').toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                    <span className="mx-2">•</span>
                    <Clock className="h-4 w-4 mr-1" />
                    <span>
                      {getTimeSpent(testResult.started_on ?? '', (DateTime.fromISO(testResult.started_on ?? '') > DateTime.fromISO(testResult.completed_on ?? '') ? testResult.expiration_time : testResult.completed_on) ?? '')}
                    </span>
                  </div>
                </div>
                
                <div className="mt-4 sm:mt-0 flex items-center">
                  <div className="rounded-full h-16 w-16 flex items-center justify-center border-4 border-indigo-100 bg-white">
                    <span className={`text-xl font-bold ${getScoreColor(testResult.score ?? 0)}`}>
                      {testResult.score}
                    </span>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm text-gray-500">Overall Score</div>
                    <div className="text-lg font-medium text-gray-900">
                      {getScoreLabel(testResult.score ?? 0)}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Section performance */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Section Performance</h3>
                <div className="space-y-4">
                  {sectionPerformance.map((section, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium text-gray-900">{section.name}</h4>
                        <div className={`font-bold ${getScoreColor(section.score)}`}>
                          {section.score}%
                        </div>
                      </div>
                      <div className="relative pt-1">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span>Correct: {section.correctCount}/{section.totalCount}</span>
                        </div>
                        <div className="flex h-2 overflow-hidden rounded bg-gray-200">
                          <div 
                            className={getProgressBarColor(section.score)} 
                            style={{ width: `${section.score}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Question list */}
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-4">Question Review</h3>
                {isLoadingTestQuestions ? (
                  <p className="text-gray-500">Loading questions...</p>
                ) : (
                  <div className="space-y-2">
                    {testQuestions?.map((testQuestion, index) => {
                      // Get user's answer and correct status from sections
                      const questionData = testResult.tests.questions;
                      const isCorrect = questionData?.correct;
                      
                      return (
                        <div
                          key={testQuestion.id}
                          onClick={() => handleQuestionClick(testQuestion.passages.id)}
                          className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <span className="font-medium text-gray-900 mr-2">Q{index + 1}.</span>
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {isCorrect ? (
                                  <CheckCircle className="h-4 w-4" />
                                ) : (
                                  <span className="text-xs">✗</span>
                                )}
                              </div>
                              <span className="ml-2 text-sm text-gray-800 line-clamp-1">
                                {testQuestion.passages.prompt.substring(0, 60)}...
                              </span>
                            </div>
                            <Eye className="h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="lg:w-1/3 mt-8 lg:mt-0">
            {/* Summary */}
            <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Test Summary</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                  <span className="text-gray-600">Category</span>
                  <span className="font-medium">{testResult.tests.category}</span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                  <span className="text-gray-600">Level</span>
                  <span className="font-medium">{testResult.tests.level}</span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                  <span className="text-gray-600">Duration</span>
                  <span className="font-medium">{getTimeSpent(testResult.started_on ?? '', (DateTime.fromISO(testResult.started_on ?? '') > DateTime.fromISO(testResult.completed_on ?? '') ? testResult.expiration_time : testResult.completed_on) ?? '')}</span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                  <span className="text-gray-600">Completion Date</span>
                  <span className="font-medium">
                    {new Date(testResult.completed_on ?? '').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Strengths and Weaknesses */}
            {/* <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Strengths & Weaknesses</h3>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                  <ArrowUp className="h-4 w-4 text-green-500 mr-2" />
                  Strengths
                </h4>
                <ul className="list-disc list-inside mb-4 text-gray-700 text-sm space-y-1">
                  <li>Strong performance in conditional reasoning</li>
                  <li>Good time management in logical reasoning</li>
                  <li>Effective at identifying flaws in arguments</li>
                </ul>
                
                <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                  <ArrowDown className="h-4 w-4 text-red-500 mr-2" />
                  Areas to Improve
                </h4>
                <ul className="list-disc list-inside text-gray-700 text-sm space-y-1">
                  <li>Strengthen approach to inference questions</li>
                  <li>Review parallel reasoning question strategies</li>
                  <li>Work on improving reading speed while maintaining comprehension</li>
                </ul>
              </div>
            </div> */}
            
            {/* Next steps */}
            {/* <div className="bg-white p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Recommended Next Steps</h3>
              
              <div className="space-y-3">
                <Link
                  to="/lessons/conditional-reasoning"
                  className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <h4 className="font-medium text-gray-900">Conditional Reasoning Deep Dive</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Strengthen your foundation with this focused lesson
                  </p>
                </Link>
                
                <Link
                  to="/practice/inference-questions"
                  className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <h4 className="font-medium text-gray-900">Inference Questions Practice Set</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Practice with 10 targeted questions to build skills
                  </p>
                </Link>
                
                <Link
                  to="/exams/recommended"
                  className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <h4 className="font-medium text-gray-900">Try a Focused Practice Test</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Apply your improved skills with a new assessment
                  </p>
                </Link>
              </div>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

// Question Review Page
export const ExamResultsQuestionPage: React.FC = () => {
  const { testId, questionId } = useParams<{ testId: string, questionId: string }>();
  const navigate = useNavigate();
  
  // Fetch question details
  const {
    data: questionDetail,
    isLoading: isLoadingQuestionDetail
  } = useQuery({
    queryKey: ['QuestionDetail', testId, questionId],
    queryFn: async() => {
      console.log(questionId);
      if (!questionId) return null;

      const { data, error } = await supabase
        .from('passages')
        .select(`
          *,
          questions(
            *,
            answers!answers_question_id_fkey(*)
          )
        `)
        .eq('id', questionId)
        .single();
      
      if (error) {
        console.log(error);
        return null;
      }
      
      return data;
    },
    enabled: !!questionId
  });
  
  // Fetch user's answer
  const {
    data: userAnswer,
    isLoading: isLoadingUserAnswer
  } = useQuery({
    queryKey: ['UserAnswer', testId, questionId],
    queryFn: async() => {
      if (!testId || !questionId) return null;
      
      const { data, error } = await supabase
        .from('profile_tests')
        .select('sections')
        .eq('id', testId)
        .single();
      
      if (error) {
        console.log(error);
        return null;
      }
      
      // Find the user's answer in the sections
      const sections = data.sections || {};
      for (const sectionKey in sections) {
        const section = sections[sectionKey];
        if (section.questions && section.questions[questionId]) {
          return section.questions[questionId];
        }
      }
      
      return null;
    },
    enabled: !!testId && !!questionId
  });
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        {/* Back button */}
        <button
          onClick={() => navigate(`/tests/${testId}`)}
          className="mb-6 inline-flex items-center text-indigo-600 hover:text-indigo-800"
        >
          <ChevronRight className="h-5 w-5 transform rotate-180 mr-1" />
          Back to Test Results
        </button>
        
        {isLoadingQuestionDetail ? (
          <div className="text-center py-10">
            <p className="text-gray-500">Loading question details...</p>
          </div>
        ) : !questionDetail ? (
          <div className="text-center py-10">
            <p className="text-red-500">Question not found.</p>
          </div>
        ) : (
          <div>
            {/* Question header */}
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 mr-2">
                  {questionDetail.question_type}
                </span>
                {userAnswer && (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    userAnswer.correct 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {userAnswer.correct ? 'Correct' : 'Incorrect'}
                  </span>
                )}
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-4">Question Review</h2>
            </div>
            
            {/* Passage */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Passage</h3>
              <div className="text-gray-700 whitespace-pre-line">
                {questionDetail.prompt}
              </div>
            </div>
            
            {/* Question */}
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Question</h3>
              <div className="text-gray-700 whitespace-pre-line">
                {questionDetail.question_text}
              </div>
            </div>
            
            {/* Answer choices */}
            <div className="mb-8">
              <h3 className="font-medium text-gray-900 mb-2">Answer Choices</h3>
              <div className="space-y-3">
                {questionDetail.questions?.[0]?.answers?.sort((a, b) => a.sorted - b.sorted).map((answer) => {
                  const isUserAnswer = userAnswer?.answerId === answer.id;
                  const isCorrectAnswer = questionDetail.questions[0].answer_id === answer.id;
                  
                  return (
                    <div 
                      key={answer.id}
                      className={`p-3 rounded-lg border ${
                        isCorrectAnswer
                          ? 'border-green-300 bg-green-50'
                          : isUserAnswer && !isCorrectAnswer
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                          isCorrectAnswer
                            ? 'bg-green-100 text-green-800'
                            : isUserAnswer && !isCorrectAnswer
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}>
                          {String.fromCharCode(64 + answer.sorted)}
                        </div>
                        <div>
                          <div className="text-gray-800">{answer.content}</div>
                          {isUserAnswer && (
                            <div className="mt-2 text-sm font-medium">
                              {isCorrectAnswer ? (
                                <span className="text-green-600">You selected the correct answer.</span>
                              ) : (
                                <span className="text-red-600">You selected this answer.</span>
                              )}
                            </div>
                          )}
                          {isCorrectAnswer && !isUserAnswer && (
                            <div className="mt-2 text-sm font-medium text-green-600">
                              This is the correct answer.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Explanation */}
            <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Explanation</h3>
              <div className="text-gray-700 whitespace-pre-line">
                {questionDetail.questions?.[0]?.explanation}
              </div>
            </div>
            
            {/* Hint for improvement */}
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
              <h3 className="font-medium text-gray-900 mb-2">Tip for Improvement</h3>
              <div className="text-gray-700 whitespace-pre-line">
                {questionDetail.questions?.[0]?.hint || 
                 "Focus on identifying the key elements in the passage that relate directly to the question. Practice breaking down complex language into simpler components."}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Progress Chart Page
export const ExamResultsProgressPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Fetch user's test history
  const {
    data: testHistory,
    isLoading: isLoadingTestHistory
  } = useQuery({
    queryKey: ['TestHistory'],
    queryFn: async() => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('profile_tests')
        .select(`
          id,
          test_id,
          completed_on,
          score,
          tests:test_id(
            test_name,
            category
          )
        `)
        .eq('profile_id', user.id)
        .not('completed_on', 'is', null)
        .order('completed_on', { ascending: true });
      
      if (error) {
        console.log(error);
        return [];
      }
      
      return data;
    },
    enabled: !!user?.id
  });
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white p-6 rounded-xl shadow-sm mb-8">
        {/* Back button */}
        <button
          onClick={() => navigate('/exam-results')}
          className="mb-6 inline-flex items-center text-indigo-600 hover:text-indigo-800"
        >
          <ChevronRight className="h-5 w-5 transform rotate-180 mr-1" />
          Back to Results
        </button>
        
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Your Progress Over Time</h2>
        
        {isLoadingTestHistory ? (
          <div className="text-center py-10">
            <p className="text-gray-500">Loading your progress data...</p>
          </div>
        ) : testHistory && testHistory.length > 0 ? (
          <div>
            {/* Progress chart would go here - using a placeholder */}
            <div className="aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg mb-8">
              <div className="flex items-center justify-center text-gray-500">
                [Progress Chart Visualization]
              </div>
            </div>
            
            {/* Test history table */}
            <h3 className="text-lg font-bold text-gray-800 mb-4">Test History</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Test Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {testHistory.map((test) => (
                    <tr 
                      key={test.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/exam-results/test/${test.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {test.tests.test_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {test.tests.category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(test.completed_on ?? '').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getScoreBadgeColor(test.score ?? 0)}`}>
                          {test.score}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-10 border border-dashed border-gray-300 rounded-lg">
            <BarChart2 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-xl font-medium text-gray-700 mb-1">No progress data yet</h3>
            <p className="text-gray-500 mb-4">Complete tests to start tracking your progress over time.</p>
            <Link
              to="/exams"
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Take Your First Test
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper functions
function getTimeSpent(startTime: string, endTime: string): string {
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const diffInMinutes = Math.floor((end - start) / (1000 * 60));
  
  const hours = Math.floor(diffInMinutes / 60);
  const minutes = diffInMinutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes} minutes`;
}

function getScoreColor(score: number = 0): string {
  if (score >= 170) return 'text-green-600';
  if (score >= 160) return 'text-green-500';
  if (score >= 140) return 'text-yellow-600';
  if (score >= 130) return 'text-yellow-500';
  return 'text-red-500';
}

function getScoreLabel(score: number): string {
  if (score >= 170) return 'Excellent';
  if (score >= 160) return 'Great';
  if (score >= 150) return 'Good';
  if (score >= 140) return 'Satisfactory';
  if (score >= 130) return 'Needs Improvement';
  return 'Requires Attention';
}

function getProgressBarColor(score: number): string {
  if (score >= 80) return 'bg-green-500';
  if (score >= 70) return 'bg-green-400';
  if (score >= 60) return 'bg-yellow-500';
  if (score >= 50) return 'bg-yellow-400';
  return 'bg-red-500';
}

function getScoreBadgeColor(score: number): string {
  if (score >= 170) return 'bg-green-100 text-green-800';
  if (score >= 160) return 'bg-green-100 text-green-800';
  if (score >= 150) return 'bg-yellow-100 text-yellow-800';
  if (score >= 140) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
}
